import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, Row, Col, Table, Form, Button, Tabs, Tab, Badge, Alert } from 'react-bootstrap';
import { FaChartLine, FaMoneyBillWave, FaFileInvoiceDollar, FaUserMd, FaExclamationTriangle, FaDownload } from 'react-icons/fa';
import { Line, Bar, Pie, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const FinancialReports = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Data states
  const [dashboard, setDashboard] = useState(null);
  const [profitLoss, setProfitLoss] = useState(null);
  const [revenueAnalysis, setRevenueAnalysis] = useState(null);
  const [expenseAnalysis, setExpenseAnalysis] = useState(null);
  const [outstanding, setOutstanding] = useState(null);
  const [doctorPerformance, setDoctorPerformance] = useState(null);
  const [cashFlow, setCashFlow] = useState(null);
  const [comparative, setComparative] = useState(null);
  const [balanceSheet, setBalanceSheet] = useState(null);
  const [patientRevenue, setPatientRevenue] = useState(null);
  const [serviceRevenue, setServiceRevenue] = useState(null);

  useEffect(() => {
    loadData();
  }, [dateRange]);

  const loadData = async () => {
    setLoading(true);
    try {
      const params = `?startDate=${dateRange.startDate}&endDate=${dateRange.endDate}`;
      
      const [dash, pl, rev, exp, out, doc, cash, comp, bal, pat, srv] = await Promise.all([
        axios.get(`/api/financial-reports/dashboard${params}`),
        axios.get(`/api/financial-reports/profit-loss${params}`),
        axios.get(`/api/financial-reports/revenue-analysis${params}`),
        axios.get(`/api/financial-reports/expense-analysis${params}`),
        axios.get('/api/financial-reports/outstanding'),
        axios.get(`/api/financial-reports/doctor-performance${params}`),
        axios.get(`/api/financial-reports/cash-flow${params}`),
        axios.get('/api/financial-reports/comparative'),
        axios.get(`/api/financial-reports/balance-sheet${params}`),
        axios.get(`/api/financial-reports/patient-revenue${params}`),
        axios.get(`/api/financial-reports/service-revenue${params}`)
      ]);

      setDashboard(dash.data);
      setProfitLoss(pl.data);
      setRevenueAnalysis(rev.data);
      setExpenseAnalysis(exp.data);
      setOutstanding(out.data);
      setDoctorPerformance(doc.data);
      setCashFlow(cash.data);
      setComparative(comp.data);
      setBalanceSheet(bal.data);
      setPatientRevenue(pat.data);
      setServiceRevenue(srv.data);
    } catch (err) {
      setError('Failed to load financial reports');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return parseFloat(amount || 0).toLocaleString('en-PK', { style: 'currency', currency: 'PKR' });
  };

  const exportToCSV = (data, filename) => {
    const csv = data.map(row => Object.values(row).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}.csv`;
    a.click();
  };

  const downloadPDF = async (reportType) => {
    try {
      const params = `?startDate=${dateRange.startDate}&endDate=${dateRange.endDate}`;
      const response = await axios.get(`/api/financial-reports/pdf/${reportType}${params}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${reportType}-report-${Date.now()}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('PDF download error:', error);
      setError('Failed to download PDF');
    }
  };

  // Dashboard Tab
  const renderDashboard = () => {
    if (!dashboard) return <div className="text-center py-5">Loading...</div>;

    return (
      <div>
        <div className="d-flex justify-content-end mb-3">
          <Button variant="success" size="sm" onClick={() => downloadPDF('dashboard')}>
            <FaDownload className="me-2" />Download PDF
          </Button>
        </div>
        <Row className="mb-4">
          <Col md={3}>
            <Card className="border-0 shadow-sm" style={{borderLeft: '4px solid #28a745'}}>
              <Card.Body>
                <p className="text-muted mb-1 small">TOTAL REVENUE</p>
                <h3 className="mb-0 text-success fw-bold">{formatCurrency(dashboard.revenue)}</h3>
                <small className="text-muted">Today: {formatCurrency(dashboard.todayRevenue)}</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="border-0 shadow-sm" style={{borderLeft: '4px solid #dc3545'}}>
              <Card.Body>
                <p className="text-muted mb-1 small">TOTAL EXPENSES</p>
                <h3 className="mb-0 text-danger fw-bold">{formatCurrency(dashboard.expenses)}</h3>
                <small className="text-muted">+ Salaries: {formatCurrency(dashboard.salaries)}</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="border-0 shadow-sm" style={{borderLeft: '4px solid #007bff'}}>
              <Card.Body>
                <p className="text-muted mb-1 small">NET PROFIT</p>
                <h3 className={`mb-0 fw-bold ${dashboard.netProfit >= 0 ? 'text-primary' : 'text-danger'}`}>
                  {formatCurrency(dashboard.netProfit)}
                </h3>
                <small className="text-muted">Margin: {dashboard.profitMargin}%</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="border-0 shadow-sm" style={{borderLeft: '4px solid #ffc107'}}>
              <Card.Body>
                <p className="text-muted mb-1 small">OUTSTANDING</p>
                <h3 className="mb-0 text-warning fw-bold">{formatCurrency(dashboard.outstanding)}</h3>
                <small className="text-muted">Patients: {dashboard.patientCount}</small>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <Card className="border-0 shadow-sm mb-4">
              <Card.Header className="bg-white">
                <h6 className="mb-0 fw-bold">Revenue vs Expenses</h6>
              </Card.Header>
              <Card.Body>
                <Doughnut
                  data={{
                    labels: ['Revenue', 'Expenses', 'Salaries'],
                    datasets: [{
                      data: [dashboard.revenue, dashboard.expenses, dashboard.salaries],
                      backgroundColor: ['#28a745', '#dc3545', '#ffc107']
                    }]
                  }}
                  options={{ responsive: true, maintainAspectRatio: true }}
                />
              </Card.Body>
            </Card>
          </Col>
          <Col md={6}>
            <Card className="border-0 shadow-sm mb-4">
              <Card.Header className="bg-white">
                <h6 className="mb-0 fw-bold">Monthly Performance</h6>
              </Card.Header>
              <Card.Body>
                <div className="text-center py-4">
                  <h2 className="text-success">{formatCurrency(dashboard.monthRevenue)}</h2>
                  <p className="text-muted">This Month's Revenue</p>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    );
  };

  // Profit & Loss Tab
  const renderProfitLoss = () => {
    if (!profitLoss || !profitLoss.revenue) return <div className="text-center py-5">Loading...</div>;

    return (
      <div>
        <div className="d-flex justify-content-end mb-3">
          <Button variant="success" size="sm" onClick={() => downloadPDF('profit-loss')}>
            <FaDownload className="me-2" />Download PDF
          </Button>
        </div>
        <Card className="border-0 shadow-sm mb-4">
          <Card.Header className="bg-white">
            <h5 className="mb-0 fw-bold">Profit & Loss Statement</h5>
          </Card.Header>
          <Card.Body>
            <Table bordered>
              <tbody>
                <tr className="table-success">
                  <td className="fw-bold">REVENUE</td>
                  <td className="text-end fw-bold">{formatCurrency(profitLoss.revenue?.total || 0)}</td>
                </tr>
                <tr>
                  <td className="ps-4">Doctor Fees</td>
                  <td className="text-end">{formatCurrency(profitLoss.revenue?.doctorFee || 0)}</td>
                </tr>
                <tr>
                  <td className="ps-4">Other Services</td>
                  <td className="text-end">{formatCurrency(profitLoss.revenue?.other || 0)}</td>
                </tr>
                <tr className="table-light">
                  <td className="fw-bold">GROSS PROFIT</td>
                  <td className="text-end fw-bold">{formatCurrency(profitLoss.grossProfit || 0)}</td>
                </tr>
                <tr className="table-danger">
                  <td className="fw-bold">EXPENSES</td>
                  <td className="text-end fw-bold">{formatCurrency(profitLoss.expenses?.total || 0)}</td>
                </tr>
                {(profitLoss.expenses?.breakdown || []).map((exp, i) => (
                  <tr key={i}>
                    <td className="ps-4">{exp.type}</td>
                    <td className="text-end">{formatCurrency(exp.amount)}</td>
                  </tr>
                ))}
                <tr>
                  <td className="fw-bold">Staff Salaries</td>
                  <td className="text-end">{formatCurrency(profitLoss.salaries || 0)}</td>
                </tr>
                <tr className="table-light">
                  <td className="fw-bold">TOTAL COSTS</td>
                  <td className="text-end fw-bold">{formatCurrency(profitLoss.totalCosts || 0)}</td>
                </tr>
                <tr className={(profitLoss.netProfit || 0) >= 0 ? 'table-primary' : 'table-danger'}>
                  <td className="fw-bold">NET PROFIT/LOSS</td>
                  <td className="text-end fw-bold">{formatCurrency(profitLoss.netProfit || 0)}</td>
                </tr>
                <tr>
                  <td className="fw-bold">PROFIT MARGIN</td>
                  <td className="text-end fw-bold">{profitLoss.profitMargin || 0}%</td>
                </tr>
              </tbody>
            </Table>
          </Card.Body>
        </Card>

        <Row>
          <Col md={6}>
            <Card className="border-0 shadow-sm">
              <Card.Header className="bg-white">
                <h6 className="mb-0 fw-bold">Revenue Breakdown</h6>
              </Card.Header>
              <Card.Body>
                <Pie
                  data={{
                    labels: ['Doctor Fees', 'Other'],
                    datasets: [{
                      data: [
                        profitLoss.revenue?.doctorFee || 0,
                        profitLoss.revenue?.other || 0
                      ],
                      backgroundColor: ['#28a745', '#6c757d']
                    }]
                  }}
                />
              </Card.Body>
            </Card>
          </Col>
          <Col md={6}>
            <Card className="border-0 shadow-sm">
              <Card.Header className="bg-white">
                <h6 className="mb-0 fw-bold">Expense Breakdown</h6>
              </Card.Header>
              <Card.Body>
                <Pie
                  data={{
                    labels: (profitLoss.expenses?.breakdown || []).map(e => e.type),
                    datasets: [{
                      data: (profitLoss.expenses?.breakdown || []).map(e => e.amount),
                      backgroundColor: ['#dc3545', '#fd7e14', '#ffc107', '#20c997', '#6610f2']
                    }]
                  }}
                />
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    );
  };

  // Revenue Analysis Tab
  const renderRevenueAnalysis = () => {
    if (!revenueAnalysis || !revenueAnalysis.timeline) return <div className="text-center py-5">Loading...</div>;

    return (
      <div>
        <Card className="border-0 shadow-sm mb-4">
          <Card.Header className="bg-white">
            <h6 className="mb-0 fw-bold">Revenue Timeline</h6>
          </Card.Header>
          <Card.Body>
            <Line
              data={{
                labels: (revenueAnalysis.timeline || []).map(t => t.date),
                datasets: [{
                  label: 'Revenue',
                  data: (revenueAnalysis.timeline || []).map(t => t.revenue),
                  borderColor: '#28a745',
                  backgroundColor: 'rgba(40, 167, 69, 0.1)',
                  tension: 0.4
                }]
              }}
              options={{
                responsive: true,
                plugins: {
                  legend: { display: true },
                  title: { display: false }
                }
              }}
            />
          </Card.Body>
        </Card>

        <Row>
          <Col md={6}>
            <Card className="border-0 shadow-sm mb-4">
              <Card.Header className="bg-white">
                <h6 className="mb-0 fw-bold">Revenue by Doctor</h6>
              </Card.Header>
              <Card.Body>
                <Table hover>
                  <thead>
                    <tr>
                      <th>Doctor</th>
                      <th className="text-end">Patients</th>
                      <th className="text-end">Revenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(revenueAnalysis.byDoctor || []).map((d, i) => (
                      <tr key={i}>
                        <td>{d.doctor}</td>
                        <td className="text-end">{d.patients}</td>
                        <td className="text-end fw-bold text-success">{formatCurrency(d.revenue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Col>
          <Col md={6}>
            <Card className="border-0 shadow-sm mb-4">
              <Card.Header className="bg-white">
                <h6 className="mb-0 fw-bold">Revenue by Payment Method</h6>
              </Card.Header>
              <Card.Body>
                <Doughnut
                  data={{
                    labels: (revenueAnalysis.byPaymentMethod || []).map(m => m.method),
                    datasets: [{
                      data: (revenueAnalysis.byPaymentMethod || []).map(m => m.revenue),
                      backgroundColor: ['#28a745', '#17a2b8', '#ffc107']
                    }]
                  }}
                />
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    );
  };

  // Outstanding Tab
  const renderOutstanding = () => {
    if (!outstanding || !outstanding.aging) return <div className="text-center py-5">Loading...</div>;

    return (
      <div>
        <Row className="mb-4">
          <Col md={3}>
            <Card className="border-0 shadow-sm" style={{borderLeft: '4px solid #ffc107'}}>
              <Card.Body>
                <p className="text-muted mb-1 small">TOTAL OUTSTANDING</p>
                <h3 className="mb-0 text-warning fw-bold">{formatCurrency(outstanding.total || 0)}</h3>
                <small className="text-muted">{outstanding.count || 0} Bills</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="border-0 shadow-sm">
              <Card.Body>
                <p className="text-muted mb-1 small">0-30 DAYS</p>
                <h4 className="mb-0 fw-bold">{formatCurrency(outstanding.aging?.current || 0)}</h4>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="border-0 shadow-sm">
              <Card.Body>
                <p className="text-muted mb-1 small">30-60 DAYS</p>
                <h4 className="mb-0 fw-bold text-warning">{formatCurrency(outstanding.aging?.days30to60 || 0)}</h4>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="border-0 shadow-sm">
              <Card.Body>
                <p className="text-muted mb-1 small">60+ DAYS</p>
                <h4 className="mb-0 fw-bold text-danger">{formatCurrency((outstanding.aging?.days60to90 || 0) + (outstanding.aging?.over90 || 0))}</h4>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Card className="border-0 shadow-sm">
          <Card.Header className="bg-white d-flex justify-content-between align-items-center">
            <h6 className="mb-0 fw-bold">Outstanding Details</h6>
            <div>
              <Button size="sm" variant="outline-success" className="me-2" onClick={() => downloadPDF('outstanding')}>
                <FaDownload className="me-2" />PDF
              </Button>
              <Button size="sm" variant="outline-primary" onClick={() => exportToCSV(outstanding.details || [], 'outstanding')}>
                <FaDownload className="me-2" />CSV
              </Button>
            </div>
          </Card.Header>
          <Card.Body className="p-0">
            <Table hover responsive>
              <thead className="bg-light">
                <tr>
                  <th>Bill #</th>
                  <th>Patient</th>
                  <th>Contact</th>
                  <th>Doctor</th>
                  <th className="text-end">Total</th>
                  <th className="text-end">Paid</th>
                  <th className="text-end">Remaining</th>
                  <th className="text-center">Days Overdue</th>
                </tr>
              </thead>
              <tbody>
                {(outstanding.details || []).map((bill, i) => (
                  <tr key={i}>
                    <td>{bill.billNumber}</td>
                    <td>{bill.patient}</td>
                    <td>{bill.contact}</td>
                    <td>{bill.doctor}</td>
                    <td className="text-end">{formatCurrency(bill.totalAmount)}</td>
                    <td className="text-end">{formatCurrency(bill.paidAmount)}</td>
                    <td className="text-end fw-bold text-warning">{formatCurrency(bill.remainingAmount)}</td>
                    <td className="text-center">
                      <Badge bg={bill.daysOverdue > 60 ? 'danger' : bill.daysOverdue > 30 ? 'warning' : 'secondary'}>
                        {bill.daysOverdue} days
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      </div>
    );
  };

  // Doctor Performance Tab
  const renderDoctorPerformance = () => {
    if (!doctorPerformance || !Array.isArray(doctorPerformance)) return <div className="text-center py-5">Loading...</div>;

    return (
      <div>
        <div className="d-flex justify-content-end mb-3">
          <Button variant="success" size="sm" onClick={() => downloadPDF('doctor-performance')}>
            <FaDownload className="me-2" />Download PDF
          </Button>
        </div>
        <Card className="border-0 shadow-sm mb-4">
          <Card.Header className="bg-white">
            <h6 className="mb-0 fw-bold">Doctor Performance Comparison</h6>
          </Card.Header>
          <Card.Body>
            <Bar
              data={{
                labels: (doctorPerformance || []).map(d => d.doctor),
                datasets: [
                  {
                    label: 'Total Billed',
                    data: (doctorPerformance || []).map(d => d.totalBilled || 0),
                    backgroundColor: '#28a745'
                  },
                  {
                    label: 'Total Collected',
                    data: (doctorPerformance || []).map(d => d.totalCollected || 0),
                    backgroundColor: '#17a2b8'
                  },
                  {
                    label: 'Discount Given',
                    data: (doctorPerformance || []).map(d => d.totalDiscount || 0),
                    backgroundColor: '#ffc107'
                  }
                ]
              }}
              options={{
                responsive: true,
                plugins: {
                  legend: { display: true }
                }
              }}
            />
          </Card.Body>
        </Card>

        <Card className="border-0 shadow-sm">
          <Card.Header className="bg-white">
            <h6 className="mb-0 fw-bold">Detailed Performance</h6>
          </Card.Header>
          <Card.Body className="p-0">
            <Table hover responsive>
              <thead className="bg-light">
                <tr>
                  <th>Doctor</th>
                  <th>Specialization</th>
                  <th className="text-end">Patients</th>
                  <th className="text-end">Total Billed</th>
                  <th className="text-end">Collected</th>
                  <th className="text-end">Discount</th>
                  <th className="text-end">Outstanding</th>
                  <th className="text-end">Avg Bill</th>
                </tr>
              </thead>
              <tbody>
                {(doctorPerformance || []).map((doc, i) => (
                  <tr key={i}>
                    <td className="fw-bold">{doc.doctor}</td>
                    <td><Badge bg="info">{doc.specialization}</Badge></td>
                    <td className="text-end">{doc.patients}</td>
                    <td className="text-end">{formatCurrency(doc.totalBilled)}</td>
                    <td className="text-end fw-bold text-success">{formatCurrency(doc.totalCollected)}</td>
                    <td className="text-end text-warning">{formatCurrency(doc.totalDiscount)}</td>
                    <td className="text-end text-danger">{formatCurrency(doc.outstanding)}</td>
                    <td className="text-end">{formatCurrency(doc.avgBillAmount)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      </div>
    );
  };

  // Cash Flow Tab
  const renderCashFlow = () => {
    if (!cashFlow || !cashFlow.timeline) return <div className="text-center py-5">Loading...</div>;

    return (
      <div>
        <Row className="mb-4">
          <Col md={4}>
            <Card className="border-0 shadow-sm" style={{borderLeft: '4px solid #28a745'}}>
              <Card.Body>
                <p className="text-muted mb-1 small">TOTAL INFLOW</p>
                <h3 className="mb-0 text-success fw-bold">{formatCurrency(cashFlow.totalInflow || 0)}</h3>
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <Card className="border-0 shadow-sm" style={{borderLeft: '4px solid #dc3545'}}>
              <Card.Body>
                <p className="text-muted mb-1 small">TOTAL OUTFLOW</p>
                <h3 className="mb-0 text-danger fw-bold">{formatCurrency(cashFlow.totalOutflow || 0)}</h3>
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <Card className="border-0 shadow-sm" style={{borderLeft: '4px solid #007bff'}}>
              <Card.Body>
                <p className="text-muted mb-1 small">NET CASH FLOW</p>
                <h3 className={`mb-0 fw-bold ${(cashFlow.netCashFlow || 0) >= 0 ? 'text-primary' : 'text-danger'}`}>
                  {formatCurrency(cashFlow.netCashFlow || 0)}
                </h3>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Card className="border-0 shadow-sm">
          <Card.Header className="bg-white">
            <h6 className="mb-0 fw-bold">Cash Flow Timeline</h6>
          </Card.Header>
          <Card.Body>
            <Line
              data={{
                labels: (cashFlow.timeline || []).map(t => t.date),
                datasets: [
                  {
                    label: 'Inflow',
                    data: (cashFlow.timeline || []).map(t => t.inflow || 0),
                    borderColor: '#28a745',
                    backgroundColor: 'rgba(40, 167, 69, 0.1)'
                  },
                  {
                    label: 'Outflow',
                    data: (cashFlow.timeline || []).map(t => t.outflow || 0),
                    borderColor: '#dc3545',
                    backgroundColor: 'rgba(220, 53, 69, 0.1)'
                  },
                  {
                    label: 'Balance',
                    data: (cashFlow.timeline || []).map(t => t.balance || 0),
                    borderColor: '#007bff',
                    backgroundColor: 'rgba(0, 123, 255, 0.1)'
                  }
                ]
              }}
              options={{
                responsive: true,
                plugins: {
                  legend: { display: true }
                }
              }}
            />
          </Card.Body>
        </Card>
      </div>
    );
  };

  // Comparative Analysis Tab
  const renderComparative = () => {
    if (!comparative || !comparative.comparison) return <div className="text-center py-5">Loading...</div>;

    return (
      <Card className="border-0 shadow-sm">
        <Card.Header className="bg-white">
          <h6 className="mb-0 fw-bold">Year-over-Year Comparison</h6>
        </Card.Header>
        <Card.Body>
          <Bar
            data={{
              labels: (comparative.comparison || []).map(c => c.month),
              datasets: [
                {
                  label: comparative.currentYear || 'Current Year',
                  data: (comparative.comparison || []).map(c => c.currentYear || 0),
                  backgroundColor: '#28a745'
                },
                {
                  label: comparative.lastYear || 'Last Year',
                  data: (comparative.comparison || []).map(c => c.lastYear || 0),
                  backgroundColor: '#6c757d'
                }
              ]
            }}
            options={{
              responsive: true,
              plugins: {
                legend: { display: true },
                title: {
                  display: true,
                  text: 'Monthly Revenue Comparison'
                }
              }
            }}
          />
        </Card.Body>
      </Card>
    );
  };

  // Balance Sheet Tab
  const renderBalanceSheet = () => {
    if (!balanceSheet || !balanceSheet.assets) return <div className="text-center py-5">Loading...</div>;

    const formatBalanceSheetDate = (dateString) => {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    };

    const isBalanced = balanceSheet.balanceCheck?.balanced;

    return (
      <div>
        {balanceSheet.balanceCheck && (
          <Alert variant={isBalanced ? 'success' : 'danger'} className="mb-3">
            <strong>Balance Check:</strong> Assets = {formatCurrency(balanceSheet.balanceCheck.assets)} | 
            Liabilities + Equity = {formatCurrency(balanceSheet.balanceCheck.liabilitiesAndEquity)} | 
            {isBalanced ? '✓ Balanced' : '✗ Not Balanced'}
          </Alert>
        )}
        <Card className="border-0 shadow-sm">
          <Card.Header className="bg-white">
            <h6 className="mb-0 fw-bold">
              Balance Sheet - From {formatBalanceSheetDate(balanceSheet.startDate)} to {formatBalanceSheetDate(balanceSheet.endDate)}
            </h6>
          </Card.Header>
          <Card.Body>
            <Table bordered>
              <tbody>
                <tr className="table-success">
                  <td className="fw-bold" colSpan="2">ASSETS</td>
                </tr>
                <tr>
                  <td className="ps-4">Cash in Hand</td>
                  <td className="text-end">{formatCurrency(balanceSheet.assets?.current?.cash || 0)}</td>
                </tr>
                <tr>
                  <td className="ps-4">Accounts Receivable</td>
                  <td className="text-end">{formatCurrency(balanceSheet.assets?.current?.accountsReceivable || 0)}</td>
                </tr>
                <tr className="table-light">
                  <td className="fw-bold">TOTAL ASSETS</td>
                  <td className="text-end fw-bold">{formatCurrency(balanceSheet.assets?.total || 0)}</td>
                </tr>
                <tr className="table-danger">
                  <td className="fw-bold" colSpan="2">LIABILITIES</td>
                </tr>
                <tr>
                  <td className="ps-4">Accounts Payable</td>
                  <td className="text-end">{formatCurrency(balanceSheet.liabilities?.current?.accountsPayable || 0)}</td>
                </tr>
                <tr>
                  <td className="ps-4">Salaries Payable</td>
                  <td className="text-end">{formatCurrency(balanceSheet.liabilities?.current?.salariesPayable || 0)}</td>
                </tr>
                <tr className="table-light">
                  <td className="fw-bold">TOTAL LIABILITIES</td>
                  <td className="text-end fw-bold">{formatCurrency(balanceSheet.liabilities?.total || 0)}</td>
                </tr>
                <tr className="table-primary">
                  <td className="fw-bold" colSpan="2">EQUITY</td>
                </tr>
                <tr>
                  <td className="ps-4">Retained Earnings</td>
                  <td className="text-end">{formatCurrency(balanceSheet.equity?.retainedEarnings || 0)}</td>
                </tr>
                <tr className="table-light">
                  <td className="fw-bold">TOTAL EQUITY</td>
                  <td className="text-end fw-bold">{formatCurrency(balanceSheet.equity?.total || 0)}</td>
                </tr>
                <tr className="table-info">
                  <td className="fw-bold">TOTAL LIABILITIES + EQUITY</td>
                  <td className="text-end fw-bold">{formatCurrency(balanceSheet.totalLiabilitiesAndEquity || 0)}</td>
                </tr>
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      </div>
    );
  };

  // Patient Revenue Tab
  const renderPatientRevenue = () => {
    if (!patientRevenue || !patientRevenue.patients) return <div className="text-center py-5">Loading...</div>;

    return (
      <div>
        <Row className="mb-4">
          <Col md={4}>
            <Card className="border-0 shadow-sm">
              <Card.Body>
                <p className="text-muted mb-1 small">TOTAL REVENUE</p>
                <h3 className="mb-0 text-success fw-bold">{formatCurrency(patientRevenue.totalRevenue || 0)}</h3>
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <Card className="border-0 shadow-sm">
              <Card.Body>
                <p className="text-muted mb-1 small">TOTAL PATIENTS</p>
                <h3 className="mb-0 fw-bold">{patientRevenue.patientCount || 0}</h3>
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <Card className="border-0 shadow-sm">
              <Card.Body>
                <p className="text-muted mb-1 small">AVG PER PATIENT</p>
                <h3 className="mb-0 text-primary fw-bold">
                  {formatCurrency((patientRevenue.patientCount || 0) > 0 ? (patientRevenue.totalRevenue || 0) / patientRevenue.patientCount : 0)}
                </h3>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Card className="border-0 shadow-sm">
          <Card.Header className="bg-white">
            <h6 className="mb-0 fw-bold">Patient-wise Revenue</h6>
          </Card.Header>
          <Card.Body className="p-0">
            <Table hover responsive>
              <thead className="bg-light">
                <tr>
                  <th>Patient ID</th>
                  <th>Name</th>
                  <th>Contact</th>
                  <th className="text-end">Visits</th>
                  <th className="text-end">Total Billed</th>
                  <th className="text-end">Total Paid</th>
                  <th className="text-end">Outstanding</th>
                </tr>
              </thead>
              <tbody>
                {(patientRevenue.patients || []).map((p, i) => (
                  <tr key={i}>
                    <td>{p.patientId}</td>
                    <td>{p.name}</td>
                    <td>{p.contact}</td>
                    <td className="text-end">{p.visits}</td>
                    <td className="text-end">{formatCurrency(p.totalBilled)}</td>
                    <td className="text-end fw-bold text-success">{formatCurrency(p.totalPaid)}</td>
                    <td className="text-end text-warning">{formatCurrency(p.outstanding)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      </div>
    );
  };

  // Service Revenue Tab
  const renderServiceRevenue = () => {
    if (!serviceRevenue || !serviceRevenue.services) return <div className="text-center py-5">Loading...</div>;

    return (
      <div>
        <Row className="mb-4">
          <Col md={6}>
            <Card className="border-0 shadow-sm">
              <Card.Body>
                <p className="text-muted mb-1 small">TOTAL REVENUE</p>
                <h3 className="mb-0 text-success fw-bold">{formatCurrency(serviceRevenue.totalRevenue || 0)}</h3>
              </Card.Body>
            </Card>
          </Col>
          <Col md={6}>
            <Card className="border-0 shadow-sm">
              <Card.Body>
                <p className="text-muted mb-1 small">TOTAL SERVICES</p>
                <h3 className="mb-0 fw-bold">{serviceRevenue.serviceCount || 0}</h3>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <Card className="border-0 shadow-sm mb-4">
              <Card.Header className="bg-white">
                <h6 className="mb-0 fw-bold">Service Revenue Distribution</h6>
              </Card.Header>
              <Card.Body>
                <Pie
                  data={{
                    labels: (serviceRevenue.services || []).slice(0, 5).map(s => s.service),
                    datasets: [{
                      data: (serviceRevenue.services || []).slice(0, 5).map(s => s.revenue || 0),
                      backgroundColor: ['#28a745', '#17a2b8', '#ffc107', '#dc3545', '#6c757d']
                    }]
                  }}
                />
              </Card.Body>
            </Card>
          </Col>
          <Col md={6}>
            <Card className="border-0 shadow-sm mb-4">
              <Card.Header className="bg-white">
                <h6 className="mb-0 fw-bold">Top Services</h6>
              </Card.Header>
              <Card.Body className="p-0">
                <Table hover>
                  <thead className="bg-light">
                    <tr>
                      <th>Service</th>
                      <th className="text-end">Count</th>
                      <th className="text-end">Revenue</th>
                      <th className="text-end">%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(serviceRevenue.services || []).slice(0, 10).map((s, i) => (
                      <tr key={i}>
                        <td>{s.service}</td>
                        <td className="text-end">{s.count}</td>
                        <td className="text-end fw-bold text-success">{formatCurrency(s.revenue)}</td>
                        <td className="text-end">{s.percentage}%</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    );
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2><FaChartLine className="me-2 text-primary" />Financial Reports</h2>
        <div className="d-flex gap-2">
          <Form.Control
            type="date"
            value={dateRange.startDate}
            onChange={(e) => setDateRange({...dateRange, startDate: e.target.value})}
            style={{width: '150px'}}
          />
          <Form.Control
            type="date"
            value={dateRange.endDate}
            onChange={(e) => setDateRange({...dateRange, endDate: e.target.value})}
            style={{width: '150px'}}
          />
          <Button variant="primary" onClick={loadData}>
            Refresh
          </Button>
        </div>
      </div>

      {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

      <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-4">
        <Tab eventKey="dashboard" title={<span><FaChartLine className="me-2" />Dashboard</span>}>
          {renderDashboard()}
        </Tab>
        <Tab eventKey="profitLoss" title={<span><FaMoneyBillWave className="me-2" />P&L Statement</span>}>
          {renderProfitLoss()}
        </Tab>
        <Tab eventKey="revenue" title={<span><FaFileInvoiceDollar className="me-2" />Revenue Analysis</span>}>
          {renderRevenueAnalysis()}
        </Tab>
        <Tab eventKey="outstanding" title={<span><FaExclamationTriangle className="me-2" />Outstanding</span>}>
          {renderOutstanding()}
        </Tab>
        <Tab eventKey="doctors" title={<span><FaUserMd className="me-2" />Doctor Performance</span>}>
          {renderDoctorPerformance()}
        </Tab>
        <Tab eventKey="cashFlow" title="Cash Flow">
          {renderCashFlow()}
        </Tab>
        <Tab eventKey="comparative" title="Comparative">
          {renderComparative()}
        </Tab>
        <Tab eventKey="balanceSheet" title="Balance Sheet">
          {renderBalanceSheet()}
        </Tab>
        <Tab eventKey="patientRevenue" title="Patient Revenue">
          {renderPatientRevenue()}
        </Tab>
        <Tab eventKey="serviceRevenue" title="Service Revenue">
          {renderServiceRevenue()}
        </Tab>
      </Tabs>
    </div>
  );
};

export default FinancialReports;
